import { useState, useEffect, useCallback } from 'react';

const SAVED_DECKS_KEY = 'ultraman_saved_decks';
const SESSION_DECK_KEY = 'ultraman_session_deck';
const MAX_SAVED_DECKS = 10;

/**
 * Custom hook for managing saved decks in localStorage
 * Handles both permanent saved decks and session persistence
 */
export function useSavedDecks() {
  const [savedDecks, setSavedDecks] = useState([]);

  // Load saved decks from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(SAVED_DECKS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setSavedDecks(Array.isArray(parsed) ? parsed : []);
      }
    } catch (err) {
      console.error('Failed to load saved decks:', err);
      setSavedDecks([]);
    }
  }, []);

  // Persist saved decks to localStorage whenever they change
  const persistDecks = useCallback((decks) => {
    try {
      localStorage.setItem(SAVED_DECKS_KEY, JSON.stringify(decks));
      setSavedDecks(decks);
    } catch (err) {
      console.error('Failed to save decks:', err);
    }
  }, []);

  /**
   * Save a deck to the saved decks list
   * @param {Object} deck - { name, cards, code, isAutoSave? }
   * @returns {boolean} - success status
   */
  const saveDeck = useCallback((deck) => {
    const newDeck = {
      id: Date.now().toString(),
      name: deck.name || 'Untitled Deck',
      cards: deck.cards,
      code: deck.code,
      cardCount: deck.cards.reduce((sum, c) => sum + (c.count || 1), 0),
      isAutoSave: deck.isAutoSave || false,
      autoSaveType: deck.autoSaveType || null,
      savedAt: new Date().toISOString(),
    };

    setSavedDecks(prev => {
      // If this is an auto-save, replace existing auto-save of the same type
      let updated = [...prev];
      if (deck.isAutoSave && deck.autoSaveType) {
        updated = updated.filter(d => d.autoSaveType !== deck.autoSaveType);
      } else if (deck.isAutoSave) {
        updated = updated.filter(d => !d.isAutoSave);
      }
      
      // Add new deck at the beginning
      updated = [newDeck, ...updated];
      
      // Limit to max decks
      if (updated.length > MAX_SAVED_DECKS) {
        updated = updated.slice(0, MAX_SAVED_DECKS);
      }
      
      persistDecks(updated);
      return updated;
    });

    return true;
  }, [persistDecks]);

  /**
   * Delete a saved deck by ID
   */
  const deleteDeck = useCallback((deckId) => {
    setSavedDecks(prev => {
      const updated = prev.filter(d => d.id !== deckId);
      persistDecks(updated);
      return updated;
    });
  }, [persistDecks]);

  /**
   * Rename a saved deck
   */
  const renameDeck = useCallback((deckId, newName) => {
    setSavedDecks(prev => {
      const updated = prev.map(d => 
        d.id === deckId ? { ...d, name: newName, isAutoSave: false } : d
      );
      persistDecks(updated);
      return updated;
    });
  }, [persistDecks]);

  /**
   * Get a saved deck by ID
   */
  const getDeck = useCallback((deckId) => {
    return savedDecks.find(d => d.id === deckId);
  }, [savedDecks]);

  /**
   * Save current session deck (for tab restore)
   */
  const saveSessionDeck = useCallback((cards) => {
    try {
      if (!cards || cards.length === 0) {
        localStorage.removeItem(SESSION_DECK_KEY);
        return;
      }
      
      const sessionData = {
        cards: cards.map(c => ({
          id: c.id,
          count: c.count || 1
        })),
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(SESSION_DECK_KEY, JSON.stringify(sessionData));
    } catch (err) {
      console.error('Failed to save session deck:', err);
    }
  }, []);

  /**
   * Load session deck (for tab restore)
   */
  const loadSessionDeck = useCallback(() => {
    try {
      const stored = localStorage.getItem(SESSION_DECK_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (err) {
      console.error('Failed to load session deck:', err);
    }
    return null;
  }, []);

  /**
   * Clear session deck
   */
  const clearSessionDeck = useCallback(() => {
    try {
      localStorage.removeItem(SESSION_DECK_KEY);
    } catch (err) {
      console.error('Failed to clear session deck:', err);
    }
  }, []);

  return {
    savedDecks,
    saveDeck,
    deleteDeck,
    renameDeck,
    getDeck,
    saveSessionDeck,
    loadSessionDeck,
    clearSessionDeck,
    maxDecks: MAX_SAVED_DECKS,
    canSaveMore: savedDecks.filter(d => !d.isAutoSave).length < MAX_SAVED_DECKS,
  };
}

export default useSavedDecks;
